import React, { useEffect } from "react";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { FileSearch, ClipboardCheck, Scale, ShieldCheck, Image } from "lucide-react";
import { addSchemaToHead } from "@/utils/schemaMarkup";

const AppraisalGuide: React.FC = () => {
	useSEO({
		title: "Florida Highwaymen Appraisal — Authentication & Valuation Guide",
		description: "How to get a Florida Highwaymen appraisal: documentation, photos, condition, provenance, and value factors.",
		keywords: "Florida Highwaymen appraisal, authentication, valuation, provenance, condition report",
		canonicalUrl: "/florida-highwaymen-appraisal",
		ogType: 'article',
		ogImage: "/lovable-uploads/a04e8ead-7f23-41b8-91a5-a2427d0afcb6.png"
	});

	useEffect(() => {
		const faq = {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			"mainEntity": [
				{"@type": "Question", "name": "What documents help an appraisal?", "acceptedAnswer": {"@type": "Answer", "text": "Photos of front/back, signature close-ups, any receipts, and prior appraisals."}},
				{"@type": "Question", "name": "How is value determined?", "acceptedAnswer": {"@type": "Answer", "text": "Artist, subject, size, condition, and recent market sales."}}
			]
		};
		addSchemaToHead(faq);
	}, []);

	return (
		<Layout>
			<div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
				<div className="container mx-auto px-4 py-12">
					<div className="max-w-5xl mx-auto">
						<div className="text-center mb-12">
							<h1 className="text-4xl md:text-5xl font-light text-white tracking-wide mb-4">Florida Highwaymen Appraisal</h1>
							<p className="text-white/70 font-light text-lg">What to prepare and how valuations are determined</p>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><Image className="h-5 w-5 text-emerald-400" /> What Photos to Include</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Front and back, signature close-ups, edges, and any labels or stamps on the verso.</p>
								</CardContent>
							</Card>

							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><FileSearch className="h-5 w-5 text-emerald-400" /> Provenance & Docs</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Bills of sale, prior appraisals, exhibition records, and publication references help support authenticity and value.</p>
								</CardContent>
							</Card>

							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><Scale className="h-5 w-5 text-emerald-400" /> How Value is Determined</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Artist importance, subject, size, condition, rarity, and current market comparables.</p>
								</CardContent>
							</Card>

							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><ShieldCheck className="h-5 w-5 text-emerald-400" /> Get an Appraisal</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Submit details via our <Link to="/appraisal" className="text-emerald-300 underline">appraisal request form</Link> for a professional evaluation.</p>
								</CardContent>
							</Card>
						</div>

						<Separator className="my-12 bg-white/20" />
						<div className="text-white/80 font-light">
							<p>Also see: <Link to="/collecting-florida-highwaymen-art" className="text-emerald-300 underline">Collecting guide</Link> · <Link to="/florida-highwaymen" className="text-emerald-300 underline">Movement guide</Link></p>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
};

export default AppraisalGuide;











